import React, { useState, useEffect } from 'react';
import { X, ShieldCheck, Download, Calendar, Ruler, User, Camera, FileText, Info } from 'lucide-react';



import { API_BASE } from '../config';

// const API_BASE = "http://127.0.0.1:8000";

export default function VerificationDetailsModal({ isOpen, onClose, candidate, onReverify }) {
    const [docUrl, setDocUrl] = useState(null);
    const [refUrl, setRefUrl] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchImage = async (endpoint, token) => {
        try {
            const res = await fetch(`${API_BASE}${endpoint}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) return null;
            const blob = await res.blob();
            return URL.createObjectURL(blob);
        } catch (e) {
            console.error("Failed to load image", e);
            return null;
        }
    };

    useEffect(() => {
        if (isOpen && candidate && candidate.is_verified) {
            setLoading(true);
            const token = localStorage.getItem('token');

            Promise.all([
                fetchImage(`/resumes/${candidate._id}/verification-doc`, token),
                fetchImage(`/resumes/${candidate._id}/verification-ref`, token)
            ]).then(([doc, ref]) => {
                setDocUrl(doc);
                setRefUrl(ref);
            }).finally(() => setLoading(false));

        } else {
            setDocUrl(null);
            setRefUrl(null);
        }
    }, [isOpen, candidate]);

    if (!isOpen || !candidate) return null;

    const verification = candidate.verification || {};

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[95vh]">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                            <ShieldCheck className="w-6 h-6 text-green-500 fill-current" />
                            Verification Proof (Audit)
                        </h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                            Verified identity for <strong>{candidate.name}</strong>.
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors">
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto space-y-6">
                    {/* Info Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                                <Calendar className="w-3 h-3" /> Verified On
                            </div>
                            <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                                {verification.verified_at ? new Date(verification.verified_at).toLocaleString() : 'N/A'}
                            </div>
                        </div>
                        <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                                <Ruler className="w-3 h-3" /> Match Score
                                <div className="relative group">
                                    <Info className="w-3 h-3 cursor-help text-slate-400" />
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg w-48 hidden group-hover:block z-50 text-center shadow-xl pointer-events-none">
                                        Cosine Distance Metric.<br />
                                        Lower is better.<br />
                                        0 = Exact Match.<br />
                                        &lt; 0.40 = High Confidence.<br />
                                        &lt; 0.75 = Adaptive Match.
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-slate-900"></div>
                                    </div>
                                </div>
                            </div>
                            <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                                {verification.score ? verification.score.toFixed(4) : 'N/A'}
                            </div>
                        </div>
                        <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg md:col-span-1 col-span-2">
                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                                <User className="w-3 h-3" /> Name Match Status
                            </div>
                            <div className={`text-sm font-semibold ${verification.name_match ? 'text-green-600' : 'text-amber-600'}`}>
                                {verification.name_match ? "Matched on ID" : "Manual Check Needed"}
                            </div>
                        </div>
                    </div>

                    {/* Image Comparison */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Reference Photo */}
                        <div>
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                    <Camera className="w-4 h-4" /> Reference Photo (Selfie)
                                </h3>
                                <button
                                    onClick={() => onReverify('reference', refUrl, docUrl)}
                                    className="px-3 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                >
                                    Re-Verify
                                </button>
                            </div>
                            <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 min-h-[300px] flex items-center justify-center relative group">
                                {loading ? (
                                    <div className="animate-pulse flex flex-col items-center gap-2 text-slate-400">
                                        <div className="w-8 h-8 rounded-full border-2 border-slate-300 border-t-transparent animate-spin"></div>
                                    </div>
                                ) : refUrl ? (
                                    <>
                                        <img src={refUrl} alt="Reference" className="w-full h-auto object-contain max-h-[400px]" />
                                    </>
                                ) : (
                                    <div className="text-center text-slate-400 p-4">
                                        <p>Not available (Legacy)</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ID Document */}
                        <div>
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                    <FileText className="w-4 h-4" /> ID Document (Proof)
                                </h3>
                                <button
                                    onClick={() => onReverify('identity', refUrl, docUrl)}
                                    className="px-3 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                >
                                    Re-Verify
                                </button>
                            </div>
                            <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 min-h-[300px] flex items-center justify-center relative group">
                                {loading ? (
                                    <div className="animate-pulse flex flex-col items-center gap-2 text-slate-400">
                                        <div className="w-8 h-8 rounded-full border-2 border-slate-300 border-t-transparent animate-spin"></div>
                                    </div>
                                ) : docUrl ? (
                                    <>
                                        <img src={docUrl} alt="Proof" className="w-full h-auto object-contain max-h-[400px]" />
                                        <a
                                            href={docUrl}
                                            download={`proof_${candidate.name}.jpg`}
                                            className="absolute bottom-4 right-4 bg-white/90 dark:bg-slate-800/90 p-2 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white text-slate-700"
                                            title="Download"
                                        >
                                            <Download className="w-5 h-5" />
                                        </a>
                                    </>
                                ) : (
                                    <div className="text-center text-slate-400 p-4">
                                        <p>Image not available.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Verification Insights (Accuracy Meters) */}
                    <div className="p-5 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-700">
                        <h3 className="text-base font-bold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                            <ShieldCheck className="w-5 h-5 text-brand-500" />
                            Match Accuracy Analysis
                        </h3>

                        <div className="space-y-6">
                            {/* Face Accuracy */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                        <Camera className="w-4 h-4 text-slate-400" />
                                        Face Similarity Confidence
                                    </span>
                                    <span className={`text-sm font-bold ${verification.score < 0.4 ? "text-green-600" :
                                        verification.score < 0.65 ? "text-blue-600" : "text-amber-600"
                                        }`}>
                                        {verification.score < 0.4 ? "Excellent Match" :
                                            verification.score < 0.65 ? "Good Match" : "Fair Match (Adaptive)"}
                                    </span>
                                </div>
                                <div className="h-3 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all duration-500 ${verification.score < 0.4 ? "bg-green-500" :
                                            verification.score < 0.65 ? "bg-blue-500" : "bg-amber-500"
                                            }`}
                                        style={{ width: `${Math.max(10, Math.min(100, (1 - (verification.score || 0)) * 100))}%` }}
                                    ></div>
                                </div>
                                <p className="text-xs text-slate-500 mt-1">
                                    Based on facial geometry comparison between Reference Selfie and ID Photo.
                                    {verification.score > 0.6 && " (Threshold relaxed due to confirmed name match)."}
                                </p>
                            </div>

                            {/* Name Accuracy */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                        <User className="w-4 h-4 text-slate-400" />
                                        Name Consistency Check
                                    </span>
                                    <span className={`text-sm font-bold ${verification.name_match ? "text-green-600" : "text-red-500"}`}>
                                        {verification.name_match ? "Verified (100%)" : "Mismatch Warning"}
                                    </span>
                                </div>
                                <div className="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                                    <div className={`p-2 rounded-full ${verification.name_match || (verification.score < 0.4) ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>
                                        {verification.name_match ? <ShieldCheck className="w-5 h-5" /> : (verification.score < 0.4 ? <ShieldCheck className="w-5 h-5" /> : <X className="w-5 h-5" />)}
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                            {verification.name_match ? "Name Successfully Matched" : "Name Discrepancy Detected"}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                            {verification.name_match
                                                ? `Candidate name found exactly on the provided ID document.`
                                                : "Candidate name could not be automatically confirmed on the ID."}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
